import { ui } from '../core/tunables.js';
import { iconElement, LOOKOUT_ICON } from '../content/icons.js';
import { inlineSvg, packUrl } from '../content/svg.js';
import { audio } from '../audio/audio.js';
import { applyMenuIcon, el, timeout } from './dom.js';
import { missionFor } from './placeholder-content.js';
import { VEHICLES } from '../content/vehicles.js';
import type { LoopEvent } from '../core/game/mission-director.js';
import type { Screen, ScreenContext } from './router.js';

/**
 * The two celebration beats: "got it" and "home again".
 *
 * Both were text placeholders - "✨ Got it! ✨" and "🏡 Home!" - on the two screens in the whole loop
 * whose entire job is to make her feel something. A three-year-old cannot read either one, so what she
 * actually got was a pause with nothing in it.
 *
 * One screen serves both, because they are the same beat pointed at different things:
 *
 *  - **Collected**: the item she fetched pops up huge, then the Lookout appears beside it with an arrow.
 *    That pairing is the whole idea of the return leg - *you have the thing, now take it there* - and it
 *    is the one thing in the game she has to understand without being told.
 *  - **Arrived**: the Lookout pops up and the item drops into it. The delivery, completed.
 *
 * Shared rules: no words, a burst of paw prints in the pups' colours, a sound on arrival, and it always
 * advances on its own. She wanders off mid-celebration constantly and must never come back to a screen
 * waiting for a press she does not know it wants.
 */

export type CelebrationKind = 'collected' | 'arrived';

export interface CelebrationScreenOptions {
  readonly loadIcon?: (id: string) => Promise<SVGSVGElement | null>;
  /** Loads the balloon dressing. Decoration only: a pack without it costs the balloons, never the beat. */
  readonly loadDeco?: () => Promise<SVGSVGElement | null>;
}

/** How many paw prints burst outward. Enough to feel thrown, few enough to stay cheap on a tablet. */
const BURST_PAWS = 12;

export function createCelebrationScreen(
  kind: CelebrationKind,
  options: CelebrationScreenOptions = {},
): Screen {
  const loadIcon = options.loadIcon ?? iconElement;
  const loadDeco = options.loadDeco ?? defaultLoadDeco;

  let disposed = false;
  const disposers: (() => void)[] = [];

  const seconds = kind === 'collected' ? ui.collectBeatSeconds : ui.arriveBeatSeconds;
  const done: LoopEvent =
    kind === 'collected' ? { type: 'COLLECT_BEAT_DONE' } : { type: 'ARRIVE_BEAT_DONE' };

  return {
    mount(root, ctx) {
      const mission = missionFor(ctx.state.missionId);

      /*
       * The hero is what she just achieved: the item when she picks it up, the Lookout when she gets it
       * home. The second slot is where she is going next, which is why it arrives a beat later rather
       * than at the same time - two things at once is one busy moment, one after the other is a story.
       */
      const heroId = kind === 'collected' ? (mission?.itemId ?? null) : LOOKOUT_ICON;
      const thenId = kind === 'collected' ? LOOKOUT_ICON : (mission?.itemId ?? null);

      const hero = el('div', { className: 'celebrate-hero' });
      const arrow = el('div', { className: 'celebrate-arrow', text: '→' });
      // "You have the thing, now take it *there*" - the one idea she has to grasp without being told.
      applyMenuIcon(arrow, 'arrow');
      const then = el('div', { className: 'celebrate-then' });

      /*
       * Balloons in the lower corners, from the pack's own bunch. They rise into frame under the beat
       * rather than at its centre - dressing, never a third thing to look at - and until the art lands
       * the hosts are empty and invisible, which is also what a pack with no deco leaves behind.
       */
      const balloons = (['left', 'right'] as const).map((side) =>
        el('div', { className: `party-balloons party-balloons--${side}` }),
      );

      const screen = el('div', {
        className: `screen screen--celebrate screen--celebrate-${kind}`,
        children: [
          el('div', { className: 'celebrate-burst', children: burstPaws() }),
          ...balloons,
          el('div', { className: 'celebrate-stage', children: [hero, arrow, then] }),
        ],
      });
      root.append(screen);

      void loadDeco().then((art) => {
        if (disposed || art === null) return;
        for (const host of balloons) {
          host.append(art.cloneNode(true));
          host.classList.add('is-in');
        }
      });

      audio.collect();
      if (kind === 'arrived') {
        // Home is the bigger of the two moments, so it gets the bigger sound on top of the chime.
        disposers.push(timeout(240, () => audio.fanfare()));
      }

      show(hero, heroId, 'is-in');

      /*
       * The second icon lands at the halfway point of the beat, so there is time to see it before the
       * screen moves on. Both icons are optional: a missing icon costs the picture, never the beat.
       */
      disposers.push(
        timeout(Math.round(seconds * 500), () => {
          arrow.classList.add('is-in');
          show(then, thenId, kind === 'collected' ? 'is-in' : 'is-dropping');
        }),
      );

      // Nothing here needs her to act.
      disposers.push(timeout(seconds * 1000, () => ctx.dispatch(done)));
    },

    unmount() {
      disposed = true;
      for (const dispose of disposers) dispose();
      disposers.length = 0;
    },
  };

  function show(host: HTMLElement, id: string | null, className: string): void {
    if (id === null) return;
    void loadIcon(id).then((icon) => {
      if (disposed || icon === null) return;
      host.replaceChildren(icon);
      host.classList.add(className);
    });
  }
}

/** Paw prints thrown outward from the middle, in the pups' colours. */
function burstPaws(): HTMLElement[] {
  return Array.from({ length: BURST_PAWS }, (_unused, index) => {
    const paw = el('div', { className: 'celebrate-paw', text: '\u{1F43E}' });
    const angle = (index / BURST_PAWS) * 360;
    /*
     * The pups' six colours - which only start working now the paw is a drawing. A colour emoji ignores
     * CSS `color`, so this code set six colours and threw six identical paws for the whole of M5. The
     * drawn paw fills with `currentColor`.
     */
    paw.style.color = (VEHICLES[index % VEHICLES.length] ?? VEHICLES[0]!).badgeColour;
    applyMenuIcon(paw, 'paw');
    // The animation flies each paw along its own angle; CSS reads both of these.
    paw.style.setProperty('--angle', `${angle}deg`);
    paw.style.animationDelay = `${index * 30}ms`;
    return paw;
  });
}

/** The balloon bunch the reward screen shares; both screens dress the same party. */
export function defaultLoadDeco(): Promise<SVGSVGElement | null> {
  return inlineSvg(packUrl('deco', 'balloons'));
}

/** Kept separate so the router's screen table stays a list of factories. */
export function celebrationFactory(kind: CelebrationKind): () => Screen {
  return () => createCelebrationScreen(kind);
}

export type { ScreenContext };
